import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchSuggestionDetails,
  clearSuggestionDetails,
  selectContentSuggestions,
  selectCommentSuggestions,
  selectIsLoading,
  selectSelectedSuggestion,
  selectSuggestionDetails,
  selectDetailsLoading,
  selectContentResponseType,
  selectCommentResponseType,
  fetchData,
  selectSuggestionsLoading,
  fetchSuggestions,
  setContentSuggestions,
  setCommentSuggestions,
  selectSuggestionsError,
  resetState,
  setChatbotId,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  selectThemedSuggestions,
  selectOpenAiTokens,
} from "../../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotDetailsPanel,
  ChatbotForm,
  ChatbotInput,
  ChatbotButton,
  ChatbotExpandButton,
  ChatbotDetailsHeader,
  ChatbotError,
  ChatbotSuggestions,
  SuggestionsHeader,
  SuggestionsBox,
  SuggestionItem,
  SuggestionTitle,
  SuggestionDescription,
  ChatbotDetails,
  ChatbotMessage,
  AppTitle,
  LoadingElement,
  DocumentTypeSelector,
  TabContainer,
  Tab,
  CopyButton,
  ChatbotTextarea,
} from "../styled";
import ThemedPostsTab from "./ThemedPostsTab";
import LoadingPage from "../../TechnicalTabs/LoadingPage";
import ErrorPage from "../../../../mainApp/features/TechnicalTabs/ErrorPage";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { handleUnauthorizedAccess } from "../../Utility/handleUnauthorizedAccess";
import {
  SOCIAL_MEDIA_SUGGESTIONS_COST,
  SOCIAL_MEDIA_COMMENT_COST,
  SOCIAL_MEDIA_DETAILS_COST
} from "../../Utility/tokenCosts";

const AdvancedChatbot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const suggestionsLoading = useSelector(selectSuggestionsLoading);
  const suggestionsError = useSelector(selectSuggestionsError);
  const contentSuggestions = useSelector(selectContentSuggestions);
  const commentSuggestions = useSelector(selectCommentSuggestions);
  const selectedSuggestion = useSelector(selectSelectedSuggestion);
  const suggestionDetails = useSelector(selectSuggestionDetails);
  const detailsLoading = useSelector(selectDetailsLoading);
  const contentResponseType = useSelector(selectContentResponseType);
  const commentResponseType = useSelector(selectCommentResponseType);
  const themedSuggestions = useSelector(selectThemedSuggestions);
  const currentTokens = useSelector(selectOpenAiTokens);

  const [activeTab, setActiveTab] = useState("content");
  const [category, setCategory] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [contentType, setContentType] = useState("post");
  const [commentType, setCommentType] = useState("negative");
  const [userComment, setUserComment] = useState("");
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [copiedText, setCopiedText] = useState("");
  const [renderedContentType, setRenderedContentType] = useState(null);
  const [openAiTokens, setOpenAiTokensState] = useState(0);

  const validationToastId = 'social-media-bot-validation-toast';

  const platforms = [
    { value: "instagram", label: "Instagram" },
    { value: "threads", label: "Threads" },
    { value: "x", label: "X" },
    { value: "tiktok", label: "TikTok" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "youtube", label: "YouTube" },
  ];

  const commentTypes = [
    { value: "negative", label: "Komentarze negatywne" },
    { value: "positive", label: "Komentarze pozytywne" },
    { value: "sales", label: "Zachęta sprzedażowa" },
    { value: "followers", label: "Zwiększenie obserwujących" },
    { value: "engagement", label: "Zachęta do odpowiedzi" },
  ];

  const getContentTypes = () => {
    const baseTypes = [
      {
        value: "post",
        label: platform === "youtube" ? "Opis do filmu" : "Treść posta",
        platforms: ["instagram", "threads", "x", "linkedin", "youtube"],
      },
      {
        value: "title",
        label:
          platform === "tiktok"
            ? "Tytuł filmu"
            : platform === "instagram"
            ? "Tytuł rolki"
            : platform === "youtube"
            ? "Tytuł filmu"
            : "Tytuł posta",
      },
      { value: "hashtags", label: "Hashtagi" },
      {
        value: "script",
        label:
          platform === "youtube" || platform === "tiktok"
            ? "Scenariusz filmu"
            : platform === "instagram"
            ? "Scenariusz rolki"
            : "Niedostępne",
        platforms: ["youtube", "tiktok", "instagram"],
      },
      {
        value: "tips",
        label:
          platform === "youtube" || platform === "tiktok"
            ? "Wskazówki do filmu"
            : platform === "instagram"
            ? "Wskazówki do rolki"
            : "Niedostępne",
        platforms: ["youtube", "tiktok", "instagram"],
      },
    ];

    return baseTypes.filter((type) => {
      if (type.platforms) {
        return type.platforms.includes(platform);
      }
      return true;
    });
  };

  const contentTypes = getContentTypes();

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(setChatbotId("advanced"));
    dispatch(resetState());
    dispatch(fetchData());
    dispatch(clearSuggestionDetails());

    return () => {
      dispatch(resetState());
      dispatch(clearSuggestionDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn("Dokument użytkownika nie istnieje.");
          }
          await delay(600);
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          await delay(600);
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });

    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }

    const availableTypes = getContentTypes();
    const isContentTypeAvailable = availableTypes.some(
      (type) => type.value === contentType
    );

    if (!isContentTypeAvailable && availableTypes.length > 0) {
      setContentType(availableTypes[0].value);
    }
  }, [restrictedDataError, navigate, platform, contentType]);

  useEffect(() => {
    if (currentTokens !== openAiTokens) {
      setOpenAiTokensState(currentTokens);
    }
  }, [currentTokens]);

  const handleCategoryChange = (e) => setCategory(e.target.value);
  const handlePlatformChange = (e) => setPlatform(e.target.value);
  const handleContentTypeChange = (e) => setContentType(e.target.value);
  const handleCommentTypeChange = (e) => setCommentType(e.target.value);
  const handleUserCommentChange = (e) => {
    setUserComment(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

 const handleContentSubmit = (e) => {
    e.preventDefault();
    if (!category.trim()) {
      toast.error("Proszę podać kategorię.", { position: "top-center", toastId: validationToastId });
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < SOCIAL_MEDIA_SUGGESTIONS_COST) {
      toast.error("Masz za mało tokenów, aby wygenerować treści.", toastOptions);
      return;
    }

    dispatch(fetchSuggestions());
    dispatch({
      type: "FETCH_ADVANCED_SUGGESTIONS",
      payload: { category, platform, contentType },
    });
    setRenderedContentType(contentType);
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!userComment.trim()) {
      toast.error("Proszę podać komentarz do odpowiedzi.", { position: "top-center", toastId: validationToastId });
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < SOCIAL_MEDIA_COMMENT_COST) {
      toast.error("Masz za mało tokenów, aby wygenerować odpowiedź.", toastOptions);
      return;
    }

    dispatch(fetchSuggestions());
    dispatch({
      type: "FETCH_COMMENT_RESPONSE",
      payload: { comment: userComment, commentType, platform },
    });
  };

  const handleSuggestionClick = (index) => {
    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (activeTab === "content" && contentType === "hashtags") {
      return;
    }

    if (currentTokens < SOCIAL_MEDIA_DETAILS_COST) {
      toast.error("Masz za mało tokenów, aby zobaczyć szczegóły.", toastOptions);
      return;
    }

    if (activeTab === "content") {
      dispatch(fetchSuggestionDetails({ index, platform, contentType, category }));
    }
  };

  const toggleDetailsPanel = () => setIsDetailsExpanded((prev) => !prev);

  const handleCopyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(""), 2000);
    } catch (err) {
      console.error("Błąd kopiowania:", err);
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopiedText(text);
        setTimeout(() => setCopiedText(""), 2000);
      } catch (execErr) {
        console.error("Błąd kopiowania (execCommand):", execErr);
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <ErrorPage />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer>
            <ChatbotMain
              isDetailsExpanded={isDetailsExpanded}
              isDetailsPanelVisible={typeof selectedSuggestion === "number" && contentSuggestions.length > 0}
            >
              <AppTitle>Social Media Content Creator</AppTitle>

             <TabContainer>
  <Tab
    active={activeTab === "content"}
    onClick={() => !suggestionsLoading && setActiveTab("content")}
    disabled={suggestionsLoading}
  >
    Treści
  </Tab>
  <Tab
    active={activeTab === "comments"}
    onClick={() => !suggestionsLoading && setActiveTab("comments")}
    disabled={suggestionsLoading}
  >
    Komentarze
  </Tab>
  <Tab
    active={activeTab === "themed"}
    onClick={() => !suggestionsLoading && setActiveTab("themed")}
    disabled={suggestionsLoading}
  >
    Posty tematyczne
  </Tab>
</TabContainer>

              {activeTab === "content" && (
                <ChatbotForm onSubmit={handleContentSubmit}>
                  <ChatbotInput
                    type="text"
                    placeholder="Wpisz kategorię (np. fitness, gotowanie)"
                    value={category}
                    onChange={handleCategoryChange}
                  />
                  <DocumentTypeSelector
                    value={platform}
                    onChange={handlePlatformChange}
                  >
                    {platforms.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </DocumentTypeSelector>
                  <DocumentTypeSelector
                    value={contentType}
                    onChange={handleContentTypeChange}
                  >
                    {contentTypes.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </DocumentTypeSelector>
                  <ChatbotButton type="submit" disabled={suggestionsLoading} tokenCost={SOCIAL_MEDIA_SUGGESTIONS_COST}>
                    Generuj treści
                  </ChatbotButton>
                </ChatbotForm>
              )}

             {activeTab === "content" && (
  <>
    {suggestionsLoading ? (
      <LoadingElement>Ładowanie</LoadingElement>
    ) : suggestionsError ? (
      <ChatbotError>{suggestionsError}</ChatbotError>
    ) : contentSuggestions && contentResponseType === "message" ? (
      renderedContentType === "hashtags" ? (
        <ChatbotSuggestions>
          <SuggestionsHeader>Propozycje hashtagów:</SuggestionsHeader>
          <SuggestionsBox>
            <SuggestionItem style={{ cursor: "text" }}>
              <SuggestionDescription>
                {typeof contentSuggestions === "string" ? contentSuggestions : "Brak hashtagów"}
              </SuggestionDescription>
            </SuggestionItem>
          </SuggestionsBox>
        </ChatbotSuggestions>
      ) : (
        <ChatbotMessage>{contentSuggestions}</ChatbotMessage>
      )
    ) : Array.isArray(contentSuggestions) &&
      contentResponseType === "suggestions" ? (
      <ChatbotSuggestions>
        <SuggestionsHeader>
          {renderedContentType === "hashtags" ? "Propozycje hashtagów:" : "Propozycje treści:"}
        </SuggestionsHeader>
        <SuggestionsBox>
          {renderedContentType === "hashtags" && contentSuggestions.length > 0 ? (
            <SuggestionItem style={{ cursor: "text" }}>
              <SuggestionDescription>
                {contentSuggestions[0]?.description || "Brak hashtagów"}
              </SuggestionDescription>
            </SuggestionItem>
          ) : (
            contentSuggestions.map((suggestion, index) => (
              <SuggestionItem
                key={index}
                onClick={() => handleSuggestionClick(index)}
                style={{ cursor: "pointer" }}
                tokenCost={SOCIAL_MEDIA_DETAILS_COST}
              >
                {suggestion.title && (
                  <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                )}
                <SuggestionDescription>
                  {suggestion.description}
                </SuggestionDescription>
              </SuggestionItem>
            ))
          )}
        </SuggestionsBox>
      </ChatbotSuggestions>
    ) : (
      <ChatbotMessage>Brak sugestii lub wystąpił błąd.</ChatbotMessage>
    )}
  </>
)}

              {activeTab === "comments" && (
                <>
                  <ChatbotForm onSubmit={handleCommentSubmit} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DocumentTypeSelector
                      value={platform}
                      onChange={handlePlatformChange}
                    >
                      {platforms.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </DocumentTypeSelector>
                    <DocumentTypeSelector
                      value={commentType}
                      onChange={handleCommentTypeChange}
                    >
                      {commentTypes.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </DocumentTypeSelector>
                    <ChatbotButton type="submit" disabled={suggestionsLoading} komentarze tokenCost={SOCIAL_MEDIA_COMMENT_COST}>
                      Generuj odpowiedź
                    </ChatbotButton>
                  </ChatbotForm>
                  <div style={{ marginTop: "10px" }}>
                    <ChatbotTextarea
                      placeholder="Wklej tutaj komentarz, na który chcesz odpowiedzieć..."
                      value={userComment}
                      onChange={handleUserCommentChange}
                      rows={3}
                    />
                  </div>
                </>
              )}

              {activeTab === "comments" && (
                <>
                  {suggestionsLoading ? (
                    <LoadingElement>Ładowanie</LoadingElement>
                  ) : suggestionsError ? (
                    <ChatbotError>{suggestionsError}</ChatbotError>
                  ) : Array.isArray(commentSuggestions) &&
                    commentResponseType === "suggestions" ? (
                    <ChatbotSuggestions>
                      <SuggestionsHeader>Propozycje odpowiedzi:</SuggestionsHeader>
                      <SuggestionsBox>
                        {commentSuggestions.map((suggestion, index) => (
                          <SuggestionItem key={index} style={{ cursor: "text" }}>
                            <SuggestionDescription>
                              {suggestion.description}
                            </SuggestionDescription>
                          </SuggestionItem>
                        ))}
                      </SuggestionsBox>
                    </ChatbotSuggestions>
                  ) : (
                    <ChatbotMessage>
                      Wklej komentarz, aby wygenerować odpowiedź.
                    </ChatbotMessage>
                           )}
              </>
            )}

            {activeTab === "themed" && <ThemedPostsTab />}
          </ChatbotMain>

            <ChatbotDetailsHeader isDetailsExpanded={isDetailsExpanded} />

            <ChatbotExpandButton
              onClick={toggleDetailsPanel}
              isDetailsExpanded={isDetailsExpanded}
            >
              {isDetailsExpanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
            </ChatbotExpandButton>

     <ChatbotDetailsPanel isDetailsExpanded={isDetailsExpanded}>
  {typeof selectedSuggestion === "number" && selectedSuggestion >= 0 ? (
    detailsLoading ? (
      <LoadingElement>Ładowanie szczegółów</LoadingElement>
    ) : suggestionDetails ? (
      <ChatbotDetails>
        {/* Wyświetlanie tytułu sugestii, jeśli istnieje i nie jest to zakładka "Posty tematyczne" */}
        {/* {activeTab !== "themed" &&
          contentSuggestions[selectedSuggestion]?.title && (
            <h2>{contentSuggestions[selectedSuggestion].title}</h2>
          )} */}
        
        {suggestionDetails.split("\n").map((line, i) => {
          const cleanedLine = line.replace(/\*\*(.*?)\*\*/g, "$1");
          const subheadingMatch = cleanedLine.match(/^(\d+\.\s)(.+)/);
          const inspirationMatch = cleanedLine.match(/^-\s(.+)/);

                      if (!cleanedLine.trim()) {
                        return null;
                      }

                      if (subheadingMatch) {
                        return (
                          <p key={i} className="subheading">
                            {cleanedLine}
                          </p>
                        );
                      } else if (inspirationMatch) {
                        return (
                          <p key={i} className="inspiration">
                            {cleanedLine}
                          </p>
                        );
                      }

                      return <p key={i}>{cleanedLine}</p>;
                    })}
                    <CopyButton
                      onClick={() => handleCopyText(suggestionDetails)}
                      aria-label="Kopiuj treść szczegółów"
                    >
                      {copiedText === suggestionDetails
                        ? "Skopiowano!"
                        : "Kopiuj treść"}
                    </CopyButton>
                  </ChatbotDetails>
                ) : (
                  <ChatbotError>
                    Wystąpił błąd podczas ładowania szczegółów.
                  </ChatbotError>
                )
              ) : (
                <ChatbotMessage>
                  Wybierz pomysł, aby zobaczyć szczegóły.
                </ChatbotMessage>
              )}
            </ChatbotDetailsPanel>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;

    case "error":
      returned = <ErrorPage />;
      break;

    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default AdvancedChatbot;